/**
 * The browser's gate on reaching a **private address** — as a named state, never
 * a spinner (card `t_4623769a`; measured in
 * `docs/plans/evidence/http-front-door-spike.md` §3, and re-measured locally by
 * this card's harness).
 *
 * The measured shape, which is the whole reason this module exists: when the app
 * runs from a **public HTTPS origin** (`https://app.torik.ai`) and dials a hub at
 * a `http://192.168.x.x` address, Chrome 153 does not send a Private Network
 * Access preflight — it gates the call on the **`local-network-access`
 * permission**, and:
 *
 *   - `prompt` (a user who has never answered the browser's bubble) — the
 *     request is **held indefinitely**: no wire traffic, no console error, no
 *     rejection. Measured `HELD 20000 ms`, and `HELD 15000 ms × 3`.
 *   - `granted` — it answers, and in milliseconds thereafter.
 *   - `denied` — held exactly like `prompt`.
 *
 * Nothing request-side fixes that: the app cannot tell a held call from a slow
 * hub, so an unbounded dial is a spinner that never resolves **and never fails**.
 * The three things this module gives the app instead:
 *
 *   1. **does the gate apply at all** (`gatesLocalNetworkAccess`): a browser
 *      whose page is not itself on the private side, dialling a private address,
 *      with the permission API present. Electron (its own secure origin, hub
 *      calls made in the main process), Capacitor, and a page the hub itself
 *      serves on `http://<lan-ip>/` all answer *no* — for them this module is
 *      inert, and no request is added anywhere;
 *   2. **the request that makes the bubble appear is issued from the caller's
 *      gesture** (`ensureLocalNetworkAccess` runs before the connect path's first
 *      `await`), because a permission request needs transient activation — and a
 *      background dial would be held for its whole budget with nobody able to
 *      answer;
 *   3. **a bound and a name** (`LOCAL_NETWORK_ACCESS_BUDGET_MS`, verdict
 *      `permission-held`): after the budget the LAN attempt is abandoned and the
 *      surface says what the browser is waiting for, with the two honest options
 *      (allow it, or continue over the cloud). The tunnel fallback is
 *      `services/hubLocalRoute.ts`'s, unchanged — this is one more reason a LAN
 *      route may not answer, and it is stated once like the others
 *      (`services/hubRouteNotice.ts`, reason `permission`).
 *
 * The budget, and why 10 s: the prompt is on screen about as soon as the request
 * is issued, and a person who reads it and clicks takes a few seconds — 10 s is
 * two to three times that. It is also *not* an extra wait: today the same held
 * address burns the whole identity ladder silently (4 rungs × 2.5 s in
 * `services/hubIdentityLookup.ts`) and then reports `unreachable`, i.e. the same
 * ten seconds with the wrong sentence.
 *
 * Not here, deliberately: hammering the permission API per request, caching a
 * `granted` for the app run (the user can revoke it in the browser at any time —
 * the query is cheap), and asking for the permission itself (the app has no way
 * to raise Chrome's bubble except by issuing the request from a gesture, which is
 * exactly what the probe below is).
 */
import { isPrivateOrLocalHostname } from '../config/runtime';

/** The permission's own name, as `navigator.permissions.query` spells it. */
export const LOCAL_NETWORK_ACCESS_PERMISSION = 'local-network-access';

/**
 * How long a dial that the browser may be holding gets before the app stops
 * waiting and says so. See the header: this replaces the ladder's silent
 * ~10 s (4 × 2.5 s) with the same wait under its own name.
 */
export const LOCAL_NETWORK_ACCESS_BUDGET_MS = 10_000;

/**
 * The route the probe asks. The hub's own info route: public, always present,
 * answered by every hub this app knows, and tiny.
 */
export const LOCAL_NETWORK_ACCESS_PROBE_PATH = '/api/info';

/** The permission's state as this app reads it (`unsupported` = no such API here). */
export type LocalNetworkAccessState = 'granted' | 'prompt' | 'denied' | 'unsupported';

/** What a caller does with the answer. */
export type LocalNetworkAccessVerdict =
  /** Dial it: the browser is not holding this address (or has nothing to say about it). */
  | 'open'
  /** The browser is holding the request for the local-network-access permission. */
  | 'permission-held';

/** Why the verdict came out the way it did — the fact behind it, for the log. */
export type LocalNetworkAccessHow =
  /** The gate does not apply here (`unsupported` API, or the page is on the private side already). */
  | 'not-gated'
  /** The permission is already `granted`: nothing to ask, dial straight away. */
  | 'granted'
  /** The probe's request was answered inside the budget. */
  | 'answered'
  /** The probe waited the whole budget with nothing back: the browser is holding it. */
  | 'held';

export interface LocalNetworkAccessResult {
  verdict: LocalNetworkAccessVerdict;
  how: LocalNetworkAccessHow;
  /** The permission state as the browser reported it (before the probe). */
  state: LocalNetworkAccessState;
  /** The state after the probe — a bubble answered during the wait shows up here. */
  stateAfter: LocalNetworkAccessState;
  /** The URL the probe asked, or `null` when nothing was dialled. */
  url: string | null;
  /** How long the probe waited, in ms (`0` when nothing was dialled). */
  waitedMs: number;
  /**
   * Was there a user's gesture behind the probe's request? `null` when the
   * runtime cannot say (Electron, an older browser). A held request with no
   * gesture is the one case the browser cannot even ask about.
   */
  activated: boolean | null;
}

export interface LocalNetworkAccessOptions {
  /** Injected by tests; the runtime uses `fetch`. */
  fetchImpl?: typeof fetch;
  /** Injected by tests; the runtime reads `navigator.permissions`. */
  permissions?: { query: (descriptor: { name: string }) => Promise<{ state: string }> } | null;
  /** Injected by tests; the runtime reads the page's own hostname. */
  pageHost?: string | null;
  /** Injected by tests; the runtime reads `navigator.userActivation`. */
  activation?: boolean | null;
  /** How long the probe may wait. */
  budgetMs?: number;
  /** The route to ask (defaults to `LOCAL_NETWORK_ACCESS_PROBE_PATH`). */
  path?: string;
  /** A caller's own cancellation (a surface that went away). */
  signal?: AbortSignal;
}

/** The permission API, when this runtime has one and it knows this name. */
function runtimePermissions(): LocalNetworkAccessOptions['permissions'] {
  try {
    const api = (globalThis as { navigator?: { permissions?: LocalNetworkAccessOptions['permissions'] } }).navigator
      ?.permissions;
    return api && typeof api.query === 'function' ? api : null;
  } catch {
    return null;
  }
}

/** Where this page is served from, as a hostname, or `null`. */
function runtimePageHost(): string | null {
  try {
    const location = (globalThis as { location?: { hostname?: string } }).location;
    return location?.hostname ? String(location.hostname) : null;
  } catch {
    return null;
  }
}

function runtimeActivation(): boolean | null {
  try {
    const activation = (globalThis as { navigator?: { userActivation?: { isActive?: boolean } } }).navigator
      ?.userActivation;
    return typeof activation?.isActive === 'boolean' ? activation.isActive : null;
  } catch {
    return null;
  }
}

/**
 * Is this hostname on the app's own side of the network? `config/runtime`'s own
 * rule (`isPrivateOrLocalHostname`) — the app's single answer to "is this mine" —
 * read through this wrapper so an empty value is a plain "no".
 */
function isPrivateHost(hostname: string | null | undefined): boolean {
  const host = typeof hostname === 'string' ? hostname.trim() : '';
  return host.length > 0 && isPrivateOrLocalHostname(host);
}

/** `http://<ip>:<port><path>`, with an IPv6 literal bracketed. */
function probeUrl(ip: string, port: number, path: string): string {
  const host = ip.includes(':') && !ip.startsWith('[') ? `[${ip}]` : ip;
  return `http://${host}:${Number(port)}${path}`;
}

/**
 * Does this runtime gate the call in the first place?
 *
 * Three facts, all measured rather than assumed:
 *   - the address being dialled is on the private side (a public hub address is
 *     never gated);
 *   - the page is **not** itself on the private side — a page the hub serves on
 *     `http://<lan-ip>/` reaches its own network freely (measured: the identical
 *     call answered 200 in 15 ms with no prompt), and only a less-private page is
 *     asked to ask;
 *   - the permission name exists here at all (`state === 'unsupported'` is the
 *     desktop app, Capacitor and older browsers — for them nothing changes).
 */
export function gatesLocalNetworkAccess(
  targetHost: string | null | undefined,
  options: { pageHost?: string | null; state?: LocalNetworkAccessState } = {},
): boolean {
  if (options.state === 'unsupported') return false;
  if (!isPrivateHost(targetHost)) return false;
  const pageHost = options.pageHost === undefined ? runtimePageHost() : options.pageHost;
  return !isPrivateHost(pageHost);
}

/** The permission's state, or `unsupported` when this runtime has no such name. */
export async function readLocalNetworkAccessState(
  options: LocalNetworkAccessOptions = {},
): Promise<LocalNetworkAccessState> {
  const permissions = options.permissions === undefined ? runtimePermissions() : options.permissions;
  if (!permissions) return 'unsupported';
  try {
    const status = await permissions.query({ name: LOCAL_NETWORK_ACCESS_PERMISSION });
    const state = (status?.state ?? '').toLowerCase();
    return state === 'granted' || state === 'prompt' || state === 'denied' ? state : 'unsupported';
  } catch {
    // No such permission name here (`TypeError`), or a permissions API that
    // refused: either way this app is not gated by it.
    return 'unsupported';
  }
}

/**
 * The one entry point: may this call proceed, or is the browser holding it?
 *
 * Cheap and silent unless the gate applies — on Electron, Capacitor, a
 * hub-served page and a non-private address this answers `open` / `not-gated`
 * **without issuing a request**. Where it does apply:
 *
 *   - `granted` → `open` immediately (the measured 16 ms path; nothing to ask);
 *   - otherwise → **one** bounded probe at the address, which is the request
 *     that raises the browser's bubble. Answered → `open`; nothing back inside
 *     the budget → `permission-held`, which the caller words as "allow it, or
 *     continue over the cloud".
 */
export async function ensureLocalNetworkAccess(
  target: { ip: string; port: number },
  options: LocalNetworkAccessOptions = {},
): Promise<LocalNetworkAccessResult> {
  const state = await readLocalNetworkAccessState(options);
  const activation = options.activation === undefined ? runtimeActivation() : options.activation;
  const base: LocalNetworkAccessResult = {
    verdict: 'open',
    how: 'not-gated',
    state,
    stateAfter: state,
    url: null,
    waitedMs: 0,
    activated: activation,
  };

  const pageHost = options.pageHost === undefined ? undefined : options.pageHost;
  if (!gatesLocalNetworkAccess(target.ip, { pageHost, state })) return base;
  if (state === 'granted') return { ...base, how: 'granted' };

  const url = probeUrl(target.ip, target.port, options.path ?? LOCAL_NETWORK_ACCESS_PROBE_PATH);
  const budgetMs = options.budgetMs ?? LOCAL_NETWORK_ACCESS_BUDGET_MS;
  const fetchImpl = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  if (!fetchImpl) return base;

  const started = Date.now();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), budgetMs);
  const external = options.signal;
  const onExternalAbort = () => controller.abort();
  external?.addEventListener('abort', onExternalAbort, { once: true });
  let answered = false;
  try {
    // Any answer — a 200, a 4xx, a hub that does not know the route — means the
    // browser did not hold this request. The probe reads no body and decides
    // nothing about the hub: the connect path's own identity read is what does.
    await fetchImpl(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
      cache: 'no-store',
    });
    answered = true;
  } catch {
    answered = false;
  } finally {
    clearTimeout(timer);
    external?.removeEventListener('abort', onExternalAbort);
  }

  const waitedMs = Date.now() - started;
  const stateAfter = answered ? await readLocalNetworkAccessState(options) : state;
  if (answered) return { ...base, how: 'answered', stateAfter, url, waitedMs };
  if (external?.aborted) return base;
  return { ...base, verdict: 'permission-held', how: 'held', stateAfter, url, waitedMs };
}
